import { useParams, Link, Navigate } from 'react-router-dom'
import { Check, ArrowLeft } from 'lucide-react'
import Seo from '../components/Seo.jsx'
import Container from '../components/Container.jsx'
import Badge from '../components/Badge.jsx'
import CTA from '../components/CTA.jsx'
import { site } from '../data/site.js'
import { getProduct } from '../data/products.js'

export default function ProductDetail() {
  const { slug } = useParams()
  const product = getProduct(slug)

  if (!product) return <Navigate to="/products" replace />

  const Icon = product.icon

  return (
    <>
      <Seo title={product.name} description={product.summary} path={`/products/${product.slug}`} />

      <main className="pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="pointer-events-none absolute inset-0 bg-grid [mask-image:linear-gradient(to_bottom,white,transparent)]" />
          <div className="pointer-events-none absolute inset-0 glow" />
          <Container className="relative py-16">
            <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All products
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-accent/10 text-accent ring-1 ring-accent/20">
                <Icon className="h-7 w-7" />
              </span>
              <h1 className="text-3xl font-extrabold sm:text-4xl">{product.name}</h1>
              <Badge status={product.status} />
            </div>
            <p className="mt-5 max-w-2xl text-lg text-slate-400">{product.description}</p>
          </Container>
        </section>

        {/* Features */}
        <section className="py-16">
          <Container>
            <h2 className="text-2xl">Key features</h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <span className="text-sm text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        {/* Pricing */}
        <section className="pb-16">
          <Container>
            <div className="text-center">
              <span className="eyebrow">Pricing</span>
              <h2 className="mt-4 text-3xl">Simple, scalable plans</h2>
              <p className="mx-auto mt-3 max-w-xl text-slate-400">
                Start small and upgrade as you grow. All plans are hosted and maintained by us.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {product.pricing.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative flex flex-col rounded-2xl border p-7 ${
                    tier.popular
                      ? 'border-brand-400/40 bg-brand-400/10 shadow-xl shadow-brand-600/10'
                      : 'border-white/10 bg-white/[0.03] backdrop-blur-sm'
                  }`}
                >
                  {tier.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                      Most popular
                    </span>
                  )}
                  <h3 className="text-lg">{tier.name}</h3>
                  <p className="mt-1 text-sm text-slate-400">{tier.description}</p>
                  <div className="mt-5">
                    {tier.price != null ? (
                      <>
                        <span className="text-4xl font-extrabold text-white">{site.currency}{tier.price}</span>
                        <span className="text-slate-400">/{tier.period}</span>
                      </>
                    ) : (
                      <span className="text-3xl font-extrabold text-white">Custom</span>
                    )}
                  </div>
                  <ul className="mt-6 space-y-3">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`mt-7 w-full ${tier.popular ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {tier.price != null ? 'Get started' : 'Contact sales'}
                  </Link>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-slate-400">
              Prices shown are placeholders, update them in <code className="text-slate-400">src/data/products.js</code>.
            </p>
          </Container>
        </section>

        <CTA
          title={`Ready to try ${product.name}?`}
          subtitle="Get in touch to start your subscription, request a demo, or ask us anything."
          primaryLabel="Get started"
          secondaryLabel="See all products"
          secondaryTo="/products"
        />
      </main>
    </>
  )
}
